"use client"

import { useState, useEffect, useCallback } from "react"
import axios from "axios" 
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { LeaveApplicationForm } from "../../Common/LeaveApplicationForm"
import TeacherLeaveBalance from "./TeacherLeaveBalance"
import { API_ENDPOINTS } from "../../../constants/config"
import { ArrowUpDown, ChevronUp, ChevronDown, Calendar, Scale } from "lucide-react"
import Pagination from "../../Common/Pagination"

export default function LeaveApplication() {
  const [leaveApplications, setLeaveApplications] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [error, setError] = useState("")
  const [isLeaveFormOpen, setIsLeaveFormOpen] = useState(false)
  const [selectedLeave, setSelectedLeave] = useState(null)
  // Add sorting state
  const [sortColumn, setSortColumn] = useState("startDate")
  const [sortDirection, setSortDirection] = useState("desc")

  const fetchLeaveApplications = useCallback(async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.get(API_ENDPOINTS.leaveTeacher, {
        headers: { Authorization: `Bearer ${token}` },
      })

      const leaveData = response.data.data ? response.data.data : []
      setLeaveApplications(Array.isArray(leaveData) ? leaveData : [])
    } catch (error) {
      console.error("Error fetching leave applications:", error)
      setError("Failed to fetch leave applications: " + (error.response?.data?.message || error.message))
      setLeaveApplications([])
    }
  }, [])

  useEffect(() => {
    fetchLeaveApplications()
  }, [fetchLeaveApplications])

  const handleLeaveSubmit = async (leaveData) => {
    try {
      const token = localStorage.getItem("token")
      const formData = new FormData()
      formData.append("startDate", leaveData.startDate)
      formData.append("endDate", leaveData.endDate)
      formData.append("reason", leaveData.reason)
      if (leaveData.leaveType) {
        formData.append("leaveType", leaveData.leaveType)
        formData.append("duration", leaveData.duration || "full-day")
      } else {
        formData.append("type", leaveData.type || "casual")
      }
      if (leaveData.attachments) {
        leaveData.attachments.forEach((file) => {
          formData.append("attachments", file)
        })
      }

      await axios.post(API_ENDPOINTS.leave, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      setIsLeaveFormOpen(false)
      fetchLeaveApplications()
    } catch (error) {
      console.error("Error submitting leave application:", error)
      setError("Failed to submit leave application: " + (error.response?.data?.message || error.message))
    }
  }

  const handleEditLeave = async (leaveData) => {
    try {
      const token = localStorage.getItem("token")
      const formData = new FormData()
      formData.append("startDate", leaveData.startDate)
      formData.append("endDate", leaveData.endDate)
      formData.append("reason", leaveData.reason)
      if (leaveData.leaveType) {
        formData.append("leaveType", leaveData.leaveType)
        formData.append("duration", leaveData.duration || "full-day")
      } else {
        formData.append("type", leaveData.type || "casual")
      }
      if (leaveData.attachments) {
        leaveData.attachments.forEach((file) => {
          formData.append("attachments", file)
        })
      }

      await axios.put(API_ENDPOINTS.leaveById(leaveData.id), formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      setIsLeaveFormOpen(false)
      fetchLeaveApplications()
    } catch (error) {
      console.error("Error updating leave application:", error)
      setError("Failed to update leave application: " + (error.response?.data?.message || error.message))
    }
  }

  const handleDeleteLeave = async (leaveId) => {
    try {
      const token = localStorage.getItem("token")
      await axios.delete(API_ENDPOINTS.leaveById(leaveId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      fetchLeaveApplications()
    } catch (error) {
      console.error("Error deleting leave application:", error)
      setError("Failed to delete leave application: " + (error.response?.data?.message || error.message))
    }
  }

  // Add sorting function
  const handleSort = (column) => {
    const newDirection = sortColumn === column && sortDirection === "asc" ? "desc" : "asc"
    setSortColumn(column)
    setSortDirection(newDirection)
  }

  // Get sort icon based on current sort state
  const getSortIcon = (column) => {
    if (column !== sortColumn) {
      return <ArrowUpDown className="ml-1 h-4 w-4 inline text-gray-400" />
    }

    return sortDirection === "asc" ? (
      <ChevronUp className="ml-1 h-4 w-4 inline text-blue-600" />
    ) : (
      <ChevronDown className="ml-1 h-4 w-4 inline text-blue-600" />
    )
  }

  // Sort the leave applications
  const sortedLeaveApplications = [...leaveApplications].sort((a, b) => {
    let valueA, valueB

    if (sortColumn === "startDate") {
      valueA = new Date(a.startDate).getTime()
      valueB = new Date(b.startDate).getTime()
    } else if (sortColumn === "endDate") {
      valueA = new Date(a.endDate).getTime()
      valueB = new Date(b.endDate).getTime()
    } else if (sortColumn === "reason") {
      valueA = a.reason?.toLowerCase() || ""
      valueB = b.reason?.toLowerCase() || ""
    } else if (sortColumn === "type") {
      valueA = a.type?.toLowerCase() || ""
      valueB = b.type?.toLowerCase() || ""
    } else if (sortColumn === "status") {
      valueA = a.status?.toLowerCase() || ""
      valueB = b.status?.toLowerCase() || ""
    } else if (sortColumn === "attachments") {
      valueA = a.attachments?.length || 0
      valueB = b.attachments?.length || 0
    }

    if (sortDirection === "asc") {
      return typeof valueA === "number" ? valueA - valueB : valueA.localeCompare(valueB)
    } else {
      return typeof valueA === "number" ? valueB - valueA : valueB.localeCompare(valueA)
    }
  })

  const paginatedLeaveApplications = sortedLeaveApplications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <Tabs defaultValue="applications" className="w-full">
        <div className="bg-gray-100 p-4 border-b flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-800">Leave</h2>
            <TabsList className="bg-white/80 h-9">
              <TabsTrigger value="applications" className="gap-1.5 data-[state=active]:bg-[#0263cc] data-[state=active]:text-white">
                <Calendar className="h-4 w-4" />
                Applications
              </TabsTrigger>
              <TabsTrigger value="balance" className="gap-1.5 data-[state=active]:bg-[#0263cc] data-[state=active]:text-white">
                <Scale className="h-4 w-4" />
                Leave Balance
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="applications" className="m-0">
            <Button onClick={() => setIsLeaveFormOpen(true)}>Apply for Leave</Button>
          </TabsContent>
        </div>
        <TabsContent value="applications" className="m-0 p-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort("startDate")}>
                Start Date {getSortIcon("startDate")}
              </TableHead>
              <TableHead className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort("endDate")}>
                End Date {getSortIcon("endDate")}
              </TableHead>
              <TableHead className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort("reason")}>
                Reason {getSortIcon("reason")}
              </TableHead>
              <TableHead className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort("type")}>
                Type {getSortIcon("type")}
              </TableHead>
              <TableHead className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort("status")}>
                Status {getSortIcon("status")}
              </TableHead>
              <TableHead className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort("attachments")}>
                Attachments {getSortIcon("attachments")}
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedLeaveApplications.length > 0 ? (
              paginatedLeaveApplications.map((leave) => (
                <TableRow key={leave._id}>
                  <TableCell>{new Date(leave.startDate).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(leave.endDate).toLocaleDateString()}</TableCell>
                  <TableCell>{leave.reason}</TableCell>
                  <TableCell>{leave.leaveType?.name || leave.type || "—"}</TableCell>
                  <TableCell>
                    <div
                      className={`rounded-full px-3 py-1 text-sm inline-flex items-center justify-center font-medium ${
                        leave.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : leave.status === "rejected"
                            ? "bg-red-100 text-red-800"
                            : "bg-orange-100 text-orange-800"
                      }`}
                    >
                      {leave.status}
                    </div>
                  </TableCell>
                  <TableCell>
                    {leave.attachments && leave.attachments.length > 0 ? (
                      <a
                        href={leave.attachments[0].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        View Attachment
                      </a>
                    ) : (
                      "No attachments"
                    )}
                  </TableCell>
                  <TableCell>
                    {leave.status === "pending" && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedLeave(leave)
                            setIsLeaveFormOpen(true)
                          }}
                        >
                          Edit
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="destructive">
                              Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Are you sure you want to delete this leave application?
                              </AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteLeave(leave._id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan="7" className="text-center text-gray-500">
                  No leave applications found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination
          currentPage={currentPage}
          totalItems={sortedLeaveApplications.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          currentPageCount={paginatedLeaveApplications.length}
          itemLabel="leave applications"
        />
        </TabsContent>
        <TabsContent value="balance" className="m-0 p-4">
          <TeacherLeaveBalance />
        </TabsContent>
      </Tabs>

      <LeaveApplicationForm
        isOpen={isLeaveFormOpen}
        onClose={() => {
          setIsLeaveFormOpen(false)
          setSelectedLeave(null)
        }}
        onSubmit={(data) =>
          selectedLeave ? handleEditLeave({ ...data, id: selectedLeave._id }) : handleLeaveSubmit(data)
        }
        initialData={selectedLeave}
        useDynamicLeaveTypes={true}
      />
    </div>
  )
}
